import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Email,
  Login,
  Home,
} from '@mui/icons-material';
import marketingTheme from '../themes/marketingTheme';

const UserVerificationStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const result = searchParams.get('result');
  const token = searchParams.get('token');
  const error = searchParams.get('error');

  useEffect(() => {
    // Simulate a brief loading state for better UX
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getStatusContent = () => {
    switch (result) {
      case 'success':
        return {
          icon: <CheckCircle sx={{ fontSize: 64, color: '#10b981' }} />,
          title: 'Email Verified Successfully!',
          message: 'Your email address has been successfully verified. You can now access all features of your MotionLogic account.',
          severity: 'success',
          actions: [
            {
              label: 'Sign In to Your Account',
              href: '/login',
              variant: 'contained',
              color: 'primary',
              icon: <Login />,
            },
            {
              label: 'Return to Home',
              href: '/',
              variant: 'outlined',
              color: 'primary',
              icon: <Home />,
            },
          ],
        };
      
      case 'error':
      case 'invalid':
        return {
          icon: <Error sx={{ fontSize: 64, color: '#ef4444' }} />,
          title: 'Verification Failed',
          message: error || 'The verification link is invalid or has already been used. Please check your email for the correct link or request a new verification email.',
          severity: 'error',
          actions: [
            {
              label: 'Request New Verification',
              href: '/register',
              variant: 'contained',
              color: 'primary',
              icon: <Email />,
            },
            {
              label: 'Contact Support',
              href: '/contact',
              variant: 'outlined',
              color: 'primary',
            },
          ],
        };
      
      case 'expired':
        return {
          icon: <Warning sx={{ fontSize: 64, color: '#f59e0b' }} />,
          title: 'Verification Link Expired',
          message: 'Your verification link has expired for security reasons. Please request a new verification email to complete your account setup.',
          severity: 'warning',
          actions: [
            {
              label: 'Request New Verification',
              href: '/register',
              variant: 'contained',
              color: 'primary',
              icon: <Email />,
            },
            {
              label: 'Return to Home',
              href: '/',
              variant: 'outlined',
              color: 'primary',
              icon: <Home />,
            },
          ],
        };
      
      default:
        return {
          icon: <Warning sx={{ fontSize: 64, color: '#f59e0b' }} />,
          title: 'Verification Status Unknown',
          message: 'We could not determine the status of your email verification. Please try again or contact support if the issue persists.',
          severity: 'warning',
          actions: [
            {
              label: 'Try Again',
              href: '/register',
              variant: 'contained',
              color: 'primary',
            },
            {
              label: 'Contact Support',
              href: '/contact',
              variant: 'outlined',
              color: 'primary',
            },
          ],
        };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#24242a',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: '#2e2e35',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CircularProgress size={48} sx={{ color: marketingTheme.palette.primary.main, mb: 3 }} />
            <Typography variant="h6" sx={{ color: '#ffffff' }}>
              Verifying your email...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  const statusContent = getStatusContent();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#24242a',
        pt: 12,
        pb: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(237, 185, 46, 0.03) 0%, transparent 50%)',
          zIndex: 0,
        }}
      />
      
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          elevation={8}
          sx={{
            backgroundColor: '#2e2e35',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: 6 }}>
            {/* Status Icon */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              {statusContent.icon}
            </Box>

            {/* Title */}
            <Typography
              variant="h3"
              component="h1"
              sx={{
                textAlign: 'center',
                mb: 3,
                color: '#ffffff',
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
              }}
            >
              {statusContent.title}
            </Typography>

            {/* Alert Message */}
            <Alert
              severity={statusContent.severity}
              sx={{
                mb: 4,
                backgroundColor: statusContent.severity === 'success' 
                  ? 'rgba(16, 185, 129, 0.1)' 
                  : statusContent.severity === 'error'
                  ? 'rgba(239, 68, 68, 0.1)'
                  : 'rgba(245, 158, 11, 0.1)',
                border: `1px solid ${
                  statusContent.severity === 'success' 
                    ? '#10b981' 
                    : statusContent.severity === 'error'
                    ? '#ef4444'
                    : '#f59e0b'
                }`,
                color: '#ffffff',
                '& .MuiAlert-icon': {
                  color: statusContent.severity === 'success' 
                    ? '#10b981' 
                    : statusContent.severity === 'error'
                    ? '#ef4444'
                    : '#f59e0b',
                },
              }}
            >
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                {statusContent.message}
              </Typography>
            </Alert>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {statusContent.actions.map((action, index) => (
                <Button
                  key={index}
                  component={Link}
                  to={action.href}
                  variant={action.variant}
                  color={action.color}
                  size="large"
                  startIcon={action.icon}
                  sx={{
                    minWidth: 200,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    ...(action.variant === 'contained' && {
                      backgroundColor: marketingTheme.palette.primary.main,
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: marketingTheme.palette.primary.dark,
                      },
                    }),
                    ...(action.variant === 'outlined' && {
                      borderColor: marketingTheme.palette.primary.main,
                      color: marketingTheme.palette.primary.main,
                      '&:hover': {
                        borderColor: marketingTheme.palette.primary.dark,
                        backgroundColor: 'rgba(237, 185, 46, 0.1)',
                      },
                    }),
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>

            {/* Additional Help */}
            <Box sx={{ textAlign: 'center', mt: 4, pt: 4, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                Need additional help?
              </Typography>
              <Typography variant="body2">
                <Link
                  to="/contact"
                  style={{
                    color: marketingTheme.palette.primary.main,
                    textDecoration: 'none',
                  }}
                >
                  Contact our support team
                </Link>
                {' or visit our '}
                <Link
                  to="/resource-library"
                  style={{
                    color: marketingTheme.palette.primary.main,
                    textDecoration: 'none',
                  }}
                >
                  help center
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Additional Information for Success Case */}
        {result === 'success' && (
          <Box sx={{ mt: 4 }}>
            <Paper
              sx={{
                p: 4,
                backgroundColor: 'rgba(45, 45, 51, 0.5)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: marketingTheme.palette.primary.main, mb: 2 }}>
                What's Next?
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
                Now that your email is verified, you can:
              </Typography>
              <Box component="ul" sx={{ color: 'rgba(255, 255, 255, 0.8)', pl: 3 }}>
                <li>
                  <Typography variant="body2" component="span">
                    Access your personalized MotionLogic dashboard
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" component="span">
                    Try our interactive demo with your industrial data
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" component="span">
                    Build a customized business case for your operations
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" component="span">
                    Connect with our implementation specialists
                  </Typography>
                </li>
              </Box>
            </Paper>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default UserVerificationStatus;
